import React, { useState, useEffect, useContext } from 'react';
import { Row, Col, Label, FormGroup, Button, Modal, ModalHeader, ModalBody, ModalFooter, Progress } from 'reactstrap';
import { DropDownInput } from '../../components/inputs/DropDownInput';
import DateField from '../shared/DateField/DateField';
import NotesField from '../shared/NotesField/NotesField';
import moment from 'moment';
import styles from './ContractCancellationModal.module.css';
import { useForm, Controller } from 'react-hook-form';
import SpinnerButton from '../shared/SpinnerButton/SpinnerButton';
import Dropzone from '../UploadDocs/DragandDropFileUpload';
import { ReactComponent as PaperMagnify } from '../../assets/images/paper-magnify.svg';
import { getDealerDocument, accountDocumentTypes } from '../Global/DocumentHelper';
import { SessionContext } from '../../context/SessionContext';
import { ReactComponent as JpegIcon } from '../../assets/images/file-jpeg.svg';
import { ReactComponent as JpgIcon } from '../../assets/images/file-jpg.svg';
import { ReactComponent as PdfIcon } from '../../assets/images/file-pdf.svg';
import { ReactComponent as PngIcon } from '../../assets/images/file-png.svg';

const ContractCancellationModal = ({
    toggle,
    isOpen,
    submitCancelContract,
    size = 'md',
    contract,
    accountName,
    getContract
}) => {
    const [dateError, setDateError] = useState(false);
    const [fileError, setFileError] = useState(false);
    const [fileErrorMsg, setFileErrorMsg] = useState('');
    const [fileUploadPercent, setFileUploadPercent] = useState();
    const [cancellationReasons, setCancellationReasons] = useState([]);
    const [binaryData, setBinaryData] = useState('');
    const { control, register, setValue, trigger, watch, handleSubmit } = useForm({
        defaultValues: {
            cancellationRequestDate: new Date(),
            cancellationReason: '',
            cancellationNotes: '',
            uploadedFiles: ''
        }
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const context = useContext(SessionContext);
    const profile = context.getProfile();
    const cancellationReason = watch('cancellationReason');
    const cancellationRequestDate = watch('cancellationRequestDate');
    const cancellationNotes = watch('cancellationNotes');
    const uploadedFiles = watch('uploadedFiles');

    useEffect(() => {
        getReason();
    }, []);

    const getReason = async () => {
        const response = await fetch(`Configuration/GetConfigByName/18/CancellationReason`);
        const data = await response.json();
        const reasons = JSON.parse(data?.keyValue)?.reasons?.map((reason) => {
            return {
                data: reason.id,
                display: reason.reason
            };
        });
        setCancellationReasons(reasons);
    };

    const handleInputChange = (e, name) => {
        setValue(name, e);
        trigger(name);
    };

    const handleDateChange = (val) => {
        const dateFormat = contract?.country?.toLowerCase() === 'us' ? 'MM/DD/YYYY' : 'DD/MM/YYYY';
        const isValidDate = moment(val, dateFormat).isValid();

        if (!isValidDate) {
            setDateError(false);
            setValue('cancellationRequestDate', '');
            trigger('cancellationRequestDate');
            return;
        }

        const minDate = moment().subtract(7, 'days').format(dateFormat);

        if (moment(val, dateFormat).isSameOrAfter(moment(minDate, dateFormat))) {
            setValue('cancellationRequestDate', val);
            trigger('cancellationRequestDate');
            setDateError(false);
        } else {
            setDateError(true);
        }
    };

    const getFieldRequired = () => {
        return cancellationReasons.find((data) => data.data === cancellationReason)?.display === 'Other';
    };

    const onSubmit = (data) => {
        if (!dateError) {
            handleSubmitContract();
        }
    };

    const handleSubmitContract = async () => {
        setIsSubmitting(true);

        const dateFormat = contract?.country?.toLowerCase() === 'us' ? 'MM/DD/YYYY' : 'DD/MM/YYYY';

        var model = {
            contractId: contract?.contractId,
            cancellationReasonId: cancellationReason,
            cancelRequestDate: moment(cancellationRequestDate, dateFormat).toISOString(),
            notes: cancellationNotes,
            cancellationRequestDocument: {
                documentName: uploadedFiles?.[0]?.name,
                documentData: binaryData?.split('base64,')?.[1]
            }
        };

        try {
            var response = await fetch('contract/RequestContractCancellation/hvac', {
                method: 'post',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(model)
            });

            if (response.ok && response.status === 200) {
                const result = await response.json();
                getContract();
                submitCancelContract();
                setIsSubmitting(false);
            }
        } catch (e) {
            setIsSubmitting(false);
        }
    };

    const cancelForm = () => {
        setValue('cancellationRequestDate', new Date());
        setValue('cancellationReason', '');
        setValue('cancellationNotes', '');
        setValue('uploadedFiles', []);
        setDateError(false);
        setFileError(false);
        setFileErrorMsg(false);
        toggle();
    };

    const getFileInfo = () => {
        getDealerDocument(accountDocumentTypes?.CustomerCancellation, true, profile, null, '');
    };

    function imageToBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                resolve(reader.result);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    const renderFileIcon = (name) => {
        let type = name.substr(name.lastIndexOf('.') + 1).toUpperCase();
        if (type === 'PNG') {
            return <PngIcon className={styles.file} />;
        } else if (type === 'PDF') {
            return <PdfIcon className={styles.file} />;
        } else if (type === 'JPEG') {
            return <JpegIcon className={styles.file} />;
        } else {
            return <JpgIcon className={styles.file} />;
        }
    };

    const handleFileDrop = async (files) => {
        if (Array.isArray(files) && files.length > 0) {
            let docType = files[0].name.substr(files[0].name.lastIndexOf('.') + 1).toUpperCase();
            const fileTypes = ['PNG', 'PDF', 'JPG'];
            const fileSize = files[0].size / 1024 / 1024;

            if (files.length > 1) {
                setFileError(true);
                setFileErrorMsg('Only select one file at a time.');
                return;
            }

            if (fileTypes.includes(docType) && fileSize <= 4) {
                setValue('uploadedFiles', files);
                setValue('fileType', docType);
                trigger('uploadedFiles');
                trigger('fileType');
                setFileError(false);
                const currentFile = files[0];

                const reader = new FileReader();
                const base64String = await imageToBase64(currentFile);
                setBinaryData(base64String);

                reader.addEventListener('progress', (event) => {
                    setFileUploadPercent(Math.round((event.total / event.loaded) * 100));
                });
                reader.readAsDataURL(currentFile);
            } else {
                let msg =
                    'The file is either larger than 4MB (or) is not an acceptable file type (.png, .pdf, or .jpg).';
                setFileError(true);
                setFileErrorMsg(msg);
            }
        } else {
            let msg = 'The file is either larger than 4MB (or) is not an acceptable file type (.png, .pdf, or .jpg).';
            setFileError(true);
            setFileErrorMsg(msg);
        }
    };

    return (
        <div>
            <Modal
                size={size}
                isOpen={isOpen}
                toggle={toggle}
            >
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalHeader className='mb-0'>
                        <h4 className='mb-0'>Cancel Request</h4>
                    </ModalHeader>
                    <ModalBody>
                        <Row id='cancelRequest'>
                            <Col md='12'>
                                <Row className='mb-3'>
                                    <Col>
                                        Cancellations will be processed within 1 to 2 business days. It can take up to
                                        30 days for any applicable refunds to be sent.
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md='6'>
                                        <Controller
                                            control={control}
                                            defaultValue={''}
                                            name='cancellationReason'
                                            rules={{
                                                required: 'Cancellation reason is required.'
                                            }}
                                            ref={register}
                                            render={({ field, fieldState, formState }) => (
                                                <FormGroup className={!!fieldState.error ? 'form-error' : ''}>
                                                    <Label
                                                        for='cancellationReason'
                                                        className='form-label'
                                                    >
                                                        Cancellation Reason
                                                    </Label>
                                                    <span
                                                        aria-hidden='true'
                                                        className='required-indicator'
                                                    >
                                                        *
                                                    </span>
                                                    <DropDownInput
                                                        name='reason'
                                                        options={cancellationReasons}
                                                        Value={cancellationReason}
                                                        onChangedValue={(val) =>
                                                            handleInputChange(val, 'cancellationReason')
                                                        }
                                                        placeholder='Please select...'
                                                        defaultValue={cancellationReason}
                                                    />
                                                    <p className='errorMsg'>{fieldState.error?.message}</p>
                                                </FormGroup>
                                            )}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md='6'>
                                        <Controller
                                            control={control}
                                            defaultValue={''}
                                            name='cancellationRequestDate'
                                            rules={{
                                                required: 'Cancellation request date is required.'
                                            }}
                                            ref={register}
                                            render={({ field, fieldState, formState }) => (
                                                <FormGroup
                                                    className={!!fieldState.error || dateError ? 'form-error' : ''}
                                                >
                                                    <Label
                                                        for='cancellationRequestDate'
                                                        className='form-label'
                                                    >
                                                        Cancellation Request Date
                                                    </Label>
                                                    <span
                                                        aria-hidden='true'
                                                        className='required-indicator'
                                                    >
                                                        *
                                                    </span>
                                                    <DateField
                                                        contract={{ contract }}
                                                        onDateChange={handleDateChange}
                                                        name='cancellationRequestDate'
                                                        minDate={new Date(new Date().setDate(new Date().getDate() - 7))}
                                                        maxDate={new Date()}
                                                        startDate={moment(new Date()).format('MM/DD/YYYY')}
                                                        value={cancellationRequestDate}
                                                    />
                                                    {!dateError && (
                                                        <p className='errorMsg'>{fieldState.error?.message}</p>
                                                    )}
                                                    {dateError && (
                                                        <div class='errorMsg'>Please enter the valid date.</div>
                                                    )}
                                                </FormGroup>
                                            )}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md='10'>
                                        <Controller
                                            control={control}
                                            defaultValue={''}
                                            name='cancellationNotes'
                                            ref={register}
                                            rules={{
                                                required: getFieldRequired()
                                                    ? 'Customer detailed reason is required.'
                                                    : false
                                            }}
                                            render={({ field, fieldState, formState }) => (
                                                <FormGroup className={!!fieldState.error ? 'form-error mb-0' : 'mb-0'}>
                                                    <NotesField
                                                        label='Customer Detailed Reason For Cancellation'
                                                        name='notes'
                                                        maxChars={500}
                                                        required={getFieldRequired()}
                                                        placeholder='Enter Description'
                                                        onChange={(value) =>
                                                            handleInputChange(value, 'cancellationNotes')
                                                        }
                                                        value={cancellationNotes}
                                                        id='customerdetailedreason'
                                                    />
                                                    <p className='errorMsg'>{fieldState.error?.message}</p>
                                                </FormGroup>
                                            )}
                                        />
                                    </Col>
                                </Row>
                                <>
                                    <Row>
                                        <Col className='mb-3 mt-4'>
                                            <p>
                                                {' '}
                                                To process your request, upload a written cancellation request from the
                                                customer with their signature. (Required)
                                                <a
                                                    onClick={getFileInfo}
                                                    target='_blank'
                                                    className='btnLink mr-2 ml-1'
                                                >
                                                    View Form
                                                </a>
                                            </p>
                                        </Col>
                                        <Col md='10'>
                                            {Array.isArray(uploadedFiles) && uploadedFiles.length > 0 ? (
                                                uploadedFiles.map((f, key) => (
                                                    <div className={styles.uploadProgress}>
                                                        {renderFileIcon(f.name)}
                                                        <div className={styles.progressWrapper}>
                                                            <div
                                                                key={key}
                                                                id={key}
                                                                className={styles.fileName}
                                                            >
                                                                {f.name}
                                                            </div>
                                                            <div className={styles.uploadProgressWrapper}>
                                                                <Progress
                                                                    className={styles.progress}
                                                                    value={fileUploadPercent}
                                                                />
                                                                <button
                                                                    id={'remove-btn' + key}
                                                                    onClick={() =>
                                                                        handleInputChange([], 'uploadedFiles')
                                                                    }
                                                                    aria-label='Cancel Uploaded File'
                                                                    className={styles.closeButton}
                                                                ></button>
                                                            </div>
                                                            <div className={styles.uploadPercentage}>
                                                                uploaded
                                                                <span>{fileUploadPercent}% completed</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <Controller
                                                    control={control}
                                                    defaultValue={''}
                                                    name='uploadedFiles'
                                                    ref={register}
                                                    rules={{
                                                        required: 'Written request from customer is required.'
                                                    }}
                                                    render={({ field, fieldState, formState }) => (
                                                        <>
                                                            <Dropzone onFileUpload={handleFileDrop} />
                                                            <p className='errorMsg mt-3'>{fieldState.error?.message}</p>
                                                            {fileError && (
                                                                <div className='errorMsg mt-3'>{fileErrorMsg}</div>
                                                            )}
                                                        </>
                                                    )}
                                                />
                                            )}
                                        </Col>
                                    </Row>
                                </>
                            </Col>
                        </Row>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            className='btnLink mr-2'
                            onClick={cancelForm}
                        >
                            Cancel
                        </Button>
                        <SpinnerButton
                            type='primary'
                            className='ml-2'
                            htmlType={'submit'}
                            spinning={isSubmitting}
                            disabled={isSubmitting}
                            text='Submit'
                        />
                    </ModalFooter>
                </form>
            </Modal>
        </div>
    );
};

export default ContractCancellationModal;
